import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar as CalendarIcon, Search, X } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { listRDOs, getRDOs } from "@/services/rdoStorageSupabase";
import type { RDO } from "@/services/rdoStorageService";
import { getObraById } from "@/lib/obraCache";

// RDO real -> linha de exibição do modal
interface ReportRow {
  id: string;
  obraId: string;
  title: string;
  date: string; // yyyy-mm-dd
  time: string;
  status: "approved" | "review" | "pending";
  author: string;
  content: string;
  sector: string;
}

function rdoToRow(rdo: RDO): ReportRow {
  const status =
    rdo.approved || rdo.status === "approved" || rdo.status === "signed"
      ? "approved"
      : rdo.status === "in_review"
      ? "review"
      : "pending";
  const obraNome = getObraById(rdo.obraId)?.nomeObra ?? "";
  return {
    id: rdo.id,
    obraId: rdo.obraId,
    title: `RDO ${rdo.date}`,
    date: rdo.date,
    time: rdo.createdAt ? new Date(rdo.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "",
    status,
    author: rdo.approvedBy || rdo.editHistory?.[rdo.editHistory.length - 1]?.user || "—",
    content: rdo.activities?.[0]?.activity ?? "",
    sector: obraNome,
  };
}

const statusConfig = {
  approved: { label: "Aprovado", variant: "default" as const, color: "bg-success" },
  review: { label: "Em Revisão", variant: "secondary" as const, color: "bg-warning" },
  pending: { label: "Pendente", variant: "outline" as const, color: "bg-muted" },
};

type SortOrder = "desc" | "asc";

interface AllReportsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Quando informado, lista só os RDOs desta obra; sem ele, todos do usuário */
  obraId?: string;
}

export function AllReportsModal({ open, onOpenChange, obraId }: AllReportsModalProps) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // RDOs reais (Supabase), carregados ao abrir
  const [allReports, setAllReports] = useState<ReportRow[]>([]);
  useEffect(() => {
    if (!open) return;
    let active = true;
    (obraId ? listRDOs(obraId) : getRDOs())
      .then((rdos) => { if (active) setAllReports(rdos.map(rdoToRow)); })
      .catch((e) => console.error("AllReportsModal:", e));
    return () => { active = false; };
  }, [open, obraId]);

  // Função para filtrar e ordenar relatórios
  const filteredAndSortedReports = useMemo(() => {
    let filtered = allReports;

    // Filtro por termo de busca
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(report => 
        report.title.toLowerCase().includes(searchLower) ||
        report.author.toLowerCase().includes(searchLower) ||
        report.content.toLowerCase().includes(searchLower) ||
        report.sector.toLowerCase().includes(searchLower)
      );
    }

    // Filtro por período
    if (dateRange.from || dateRange.to) {
      filtered = filtered.filter(report => {
        const reportDate = new Date(report.date);
        
        if (dateRange.from && dateRange.to) {
          return reportDate >= dateRange.from && reportDate <= dateRange.to;
        } else if (dateRange.from) {
          return reportDate >= dateRange.from;
        } else if (dateRange.to) {
          return reportDate <= dateRange.to;
        }
        
        return true;
      });
    }

    // Ordenação
    if (sortOrder === "desc") {
      filtered = [...filtered].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } else if (sortOrder === "asc") {
      filtered = [...filtered].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }

    return filtered;
  }, [searchTerm, sortOrder, dateRange]);

  // Parse manual (yyyy-mm-dd) para evitar off-by-one de timezone
  const formatDateForDisplay = (dateString: string) => {
    const [y, m, d] = dateString.split("-");
    return `${d}/${m}/${y}`;
  };

  const clearDateRange = () => {
    setDateRange({});
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Todos os Relatórios</DialogTitle>
        </DialogHeader>

        {/* Filtros */}
        <div className="flex flex-col gap-4 pb-4 border-b">
          {/* Linha 1: Busca e Ordenação */}
          <div className="flex gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Buscar relatórios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={sortOrder} onValueChange={(value: SortOrder) => setSortOrder(value)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Ordenação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Mais Recentes</SelectItem>
                <SelectItem value="asc">Mais Antigos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Linha 2: Filtro de Período */}
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Período:</span>
              <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[280px] justify-start text-left font-normal",
                      !dateRange.from && !dateRange.to && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, "dd/MM/yy", { locale: ptBR })} -{" "}
                          {format(dateRange.to, "dd/MM/yy", { locale: ptBR })}
                        </>
                      ) : (
                        format(dateRange.from, "dd/MM/yyyy", { locale: ptBR })
                      )
                    ) : (
                      <span>Selecionar período</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={{ from: dateRange.from, to: dateRange.to }}
                    onSelect={(range) => setDateRange(range || {})}
                    numberOfMonths={2}
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              
              {(dateRange.from || dateRange.to) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearDateRange}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Lista de Relatórios */}
        <div className="flex-1 overflow-y-auto space-y-2">
          {filteredAndSortedReports.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Nenhum relatório encontrado com os filtros aplicados.</p>
            </div>
          ) : (
            filteredAndSortedReports.map((report) => {
              const statusInfo = statusConfig[report.status as keyof typeof statusConfig];
              return (
                <Card
                  key={report.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    onOpenChange(false);
                    navigate(`/rdo?obra=${report.obraId}&date=${report.date}`);
                  }}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-sm">
                        <span className="font-medium text-foreground">{formatDateForDisplay(report.date)}</span>
                        {report.sector && <span className="text-muted-foreground">{report.sector}</span>}
                        <span className="text-muted-foreground">{report.time}</span>
                        <span className="text-muted-foreground">por {report.author}</span>
                      </div>
                      <Badge variant={statusInfo.variant} className="text-xs">
                        {statusInfo.label}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Rodapé com total */}
        <div className="pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            Exibindo {filteredAndSortedReports.length} de {allReports.length} relatórios
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}